/**
 * "Add team member" (DNKADM-0491; dink-api DNKAPI-0713) — one right-side drawer, one Save.
 *
 * Name, email and "What do they do?" (Owner / Manager / Front desk / Coach — a Manager sees only
 * Front desk and Coach, each with one line saying what they can do), then their locations
 * (only the caller's own — `listLocations` is caller-scoped by dink-api — preselected when
 * there is one). A Coach also picks the services they teach (may skip) and a booking calendar:
 * Create one for them (default) / Use an existing calendar (picker) / Skip for now.
 *
 * Save is ONE `POST /v2/admin/team-members` (`createTeamMember`). The answer carries every
 * step's outcome, and the drawer turns into a result list: invite sent, services added, the
 * calendar created / linked / not linked. A calendar that did not link is a partial success,
 * never one red error: "not linked yet" (no GHL scope / no GHL user) offers the existing-calendar
 * picker right here; "not linked" (failed) links to the coach's staff page to fix it.
 *
 * **Submit twice (the AddMemberDialog pattern, DNKADM-0479).** Every Save carries an
 * `idempotencyKey`, minted on the first press and kept for every re-send of that Save, dropped on
 * an answer, a definitive refusal, or any edit that changes the body. A network error, timeout,
 * 5xx or `409 CONCURRENT_REQUEST` keeps the exact body under its key and locks the form; only
 * "Check again" re-sends it, and dink-api answers a key it has seen with the stored result.
 *
 * The existing-calendar picker reads the same catalog the staff calendar-sync panel reads
 * (`GET /v2/admin/staff/{id}/calendars`). Before the coach exists there is no id of their own,
 * so the drawer reads it through any live staff record: for the GHL provider that list is the
 * tenant's one sub-account list. A per-coach provider (Google) or no provider offers nothing to
 * pick, and says so.
 */
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import type { V2Location } from '@/api/locations';
import { listServices } from '@/api/services';
import { getStaffMember, listStaff, updateStaff, type StaffInput } from '@/api/staff';
import { getStaffCalendars, type StaffCalendarCatalog } from '@/api/staffCalendar';
import { newIdempotencyKey } from '@/api/payments';
import { serverMessage } from '@/api/serverMessage';
import {
  createTeamMember,
  type CreateTeamMemberInput,
  type CreateTeamMemberResult,
  type TeamMemberCalendarMode,
  type TeamMemberKind,
} from '@/api/teamMembers';
import { outcomeIsUnknown } from '@/features/members/unconfirmedOutcome';
import { staffCalendarSyncErrorMessage } from '@/features/staff/staffError';
import { scrubBrand } from '@/utils/whitelabel';
import {
  UNCONFIRMED_TEAM_MEMBER_CLOSE_PROMPT,
  UNCONFIRMED_TEAM_MEMBER_MESSAGE,
  calendarResultLine,
  type CalendarResultLine,
  hasFirstAndLastName,
  isInFlightRefusal,
  kindChoices,
  servicesResultLine,
  teamMemberErrorMessage,
} from './teamMemberCopy';

export interface AddTeamMemberDrawerProps {
  open: boolean;
  /** Owner or human platform support: all four choices. Otherwise Front desk and Coach. */
  canAddAnyRole: boolean;
  /** The caller's own active locations. */
  locations: V2Location[];
  onClose: () => void;
  /** The add landed (fully or partly) — refresh the roster and the Invited tab. */
  onAdded: (result: CreateTeamMemberResult) => void;
}

export function AddTeamMemberDrawer({
  open,
  canAddAnyRole,
  locations,
  onClose,
  onAdded,
}: AddTeamMemberDrawerProps) {
  // The body is mounted per open, so a reopened drawer starts clean.
  const [locked, setLocked] = useState(false);
  function requestClose() {
    if (locked && !window.confirm(UNCONFIRMED_TEAM_MEMBER_CLOSE_PROMPT)) return;
    setLocked(false);
    onClose();
  }
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      slotProps={{ paper: { sx: { width: 440, maxWidth: '100vw' } } }}
    >
      {open ? (
        <AddTeamMemberBody
          canAddAnyRole={canAddAnyRole}
          locations={locations}
          onClose={requestClose}
          onAdded={onAdded}
          onLockedChange={setLocked}
        />
      ) : null}
    </Drawer>
  );
}

function AddTeamMemberBody({
  canAddAnyRole,
  locations,
  onClose,
  onAdded,
  onLockedChange,
}: {
  canAddAnyRole: boolean;
  locations: V2Location[];
  onClose: () => void;
  onAdded: (result: CreateTeamMemberResult) => void;
  onLockedChange: (locked: boolean) => void;
}) {
  const ids = useId();
  const choices = kindChoices(canAddAnyRole);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<TeamMemberKind | null>(null);
  // Preselected when the caller has exactly one location.
  const [locationIds, setLocationIds] = useState<string[]>(() =>
    locations.length === 1 && locations[0] ? [locations[0].id] : [],
  );
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [calendarMode, setCalendarMode] = useState<TeamMemberCalendarMode>('CREATE');
  const [calendarId, setCalendarId] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateTeamMemberResult | null>(null);
  /** The key of the Save being attempted (null until the first press). */
  const attemptKeyRef = useRef<string | null>(null);
  /** A Save the server never confirmed either way, kept exactly as sent. */
  const [unconfirmed, setUnconfirmed] = useState<CreateTeamMemberInput | null>(null);
  const formLocked = submitting || unconfirmed !== null;
  const isCoach = kind === 'COACH';

  useEffect(() => {
    onLockedChange(unconfirmed !== null);
  }, [unconfirmed, onLockedChange]);

  // Any edit that changes the body starts a NEW Save; only a re-send may reuse the key.
  useEffect(() => {
    attemptKeyRef.current = null;
  }, [name, email, kind, locationIds, serviceIds, calendarMode, calendarId]);

  const services = useResource(
    useCallback(() => listServices({ status: 'active' }), []),
    [],
  );

  function toggle(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  function validate(): string | null {
    if (name.trim().length === 0) return 'Enter their name.';
    if (isCoach && !hasFirstAndLastName(name)) return 'Enter a coach’s first and last name.';
    if (!/^[^\s@]+@[^\s@]+$/.test(email.trim())) return 'Enter a valid email address.';
    if (kind === null) return 'Choose what they do.';
    if (locationIds.length === 0) return 'Pick at least one location.';
    if (isCoach && calendarMode === 'EXISTING' && calendarId === '') {
      return 'Pick a calendar, or choose another booking calendar option.';
    }
    return null;
  }

  async function handleSave() {
    setFieldError(null);
    setSubmitError(null);
    const problem = validate();
    if (problem !== null || kind === null) {
      setFieldError(problem);
      return;
    }
    const key = attemptKeyRef.current ?? newIdempotencyKey();
    attemptKeyRef.current = key;
    const input: CreateTeamMemberInput = {
      name: name.trim().replace(/\s+/g, ' '),
      email: email.trim(),
      kind,
      locationIds,
      idempotencyKey: key,
    };
    if (kind === 'COACH') {
      input.serviceIds = serviceIds;
      input.calendar =
        calendarMode === 'EXISTING' ? { mode: 'EXISTING', calendarId } : { mode: calendarMode };
    }
    await send(input);
  }

  /** The one write path: the first press and every "Check again" re-send. */
  async function send(input: CreateTeamMemberInput) {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const answer = await createTeamMember(input);
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      setResult(answer);
      onAdded(answer);
    } catch (err) {
      if (outcomeIsUnknown(err, unconfirmed !== null) || isInFlightRefusal(err)) {
        // The add may have landed: keep the body under its key and lock the form.
        setUnconfirmed(input);
        return;
      }
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      setSubmitError(teamMemberErrorMessage(err, input.email));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCheckAgain() {
    if (unconfirmed === null || submitting) return;
    await send(unconfirmed);
  }

  const headerId = `${ids}-title`;
  const kindLabelId = `${ids}-kind`;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      role="dialog"
      aria-labelledby={headerId}
      data-testid="add-team-member-drawer"
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2.5,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography id={headerId} component="h2" sx={{ fontSize: 20, fontWeight: 500 }}>
          Add team member
        </Typography>
        <IconAction size="small" onClick={onClose} label="Close" disabled={submitting}>
          <Icon name="close" sx={{ fontSize: 22 }} />
        </IconAction>
      </Stack>

      {result ? (
        <ResultView result={result} onDone={onClose} />
      ) : (
        <>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
            <Stack spacing={2.5}>
              <TextField
                label="Name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={formLocked}
                fullWidth
                helperText={isCoach ? 'First and last name' : undefined}
              />
              <TextField
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={formLocked}
                fullWidth
              />

              <FormControl component="fieldset" disabled={formLocked}>
                <FormLabel id={kindLabelId} component="legend">
                  What do they do?
                </FormLabel>
                <RadioGroup
                  aria-labelledby={kindLabelId}
                  value={kind ?? ''}
                  onChange={(e) => setKind(e.target.value as TeamMemberKind)}
                >
                  {choices.map((c) => (
                    <FormControlLabel
                      key={c.kind}
                      value={c.kind}
                      control={<Radio />}
                      sx={{ alignItems: 'flex-start', mt: 0.5 }}
                      label={
                        <Box sx={{ pt: 1 }}>
                          <Typography sx={{ fontSize: 14.5, fontWeight: 500 }}>
                            {c.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {c.hint}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <FormControl component="fieldset" disabled={formLocked}>
                <FormLabel component="legend">Locations</FormLabel>
                {locations.length === 0 ? (
                  <FormHelperText>You have no locations to give access to.</FormHelperText>
                ) : (
                  locations.map((loc) => (
                    <FormControlLabel
                      key={loc.id}
                      control={
                        <Checkbox
                          checked={locationIds.includes(loc.id)}
                          onChange={() => setLocationIds((prev) => toggle(prev, loc.id))}
                        />
                      }
                      label={loc.name || loc.id}
                    />
                  ))
                )}
              </FormControl>

              {isCoach ? (
                <>
                  <FormControl component="fieldset" disabled={formLocked}>
                    <FormLabel component="legend">Services they teach</FormLabel>
                    <FormHelperText sx={{ mx: 0 }}>
                      Optional. You can add these later.
                    </FormHelperText>
                    {services.status === 'loading' ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Loading services…
                      </Typography>
                    ) : services.status === 'error' ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Services couldn’t load. You can add them later on each service.
                      </Typography>
                    ) : (services.data ?? []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        No services yet.
                      </Typography>
                    ) : (
                      (services.data ?? []).map((s) => (
                        <FormControlLabel
                          key={s.id}
                          control={
                            <Checkbox
                              checked={serviceIds.includes(s.id)}
                              onChange={() => setServiceIds((prev) => toggle(prev, s.id))}
                            />
                          }
                          label={s.name}
                        />
                      ))
                    )}
                  </FormControl>

                  <FormControl component="fieldset" disabled={formLocked}>
                    <FormLabel id={`${ids}-calendar`} component="legend">
                      Booking calendar
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby={`${ids}-calendar`}
                      value={calendarMode}
                      onChange={(e) => setCalendarMode(e.target.value as TeamMemberCalendarMode)}
                    >
                      <FormControlLabel
                        value="CREATE"
                        control={<Radio />}
                        label="Create one for them"
                      />
                      <FormControlLabel
                        value="EXISTING"
                        control={<Radio />}
                        label="Use an existing calendar"
                      />
                      <FormControlLabel value="NONE" control={<Radio />} label="Skip for now" />
                    </RadioGroup>
                    {calendarMode === 'EXISTING' ? (
                      <TenantCalendarPicker
                        value={calendarId}
                        onChange={setCalendarId}
                        disabled={formLocked}
                      />
                    ) : null}
                  </FormControl>
                </>
              ) : null}

              {fieldError ? (
                <Alert severity="error" role="alert">
                  {fieldError}
                </Alert>
              ) : null}
              {submitError ? (
                <Alert severity="error" role="alert">
                  {submitError}
                </Alert>
              ) : null}
              {unconfirmed !== null ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => void handleCheckAgain()}
                      disabled={submitting}
                    >
                      Check again
                    </Button>
                  }
                >
                  {UNCONFIRMED_TEAM_MEMBER_MESSAGE}
                </Alert>
              ) : null}
            </Stack>
          </Box>

          <Stack
            direction="row"
            sx={{ gap: 1, px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}
          >
            <Button
              color="inherit"
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={formLocked}
              sx={{ flex: 1 }}
            >
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

/**
 * The tenant's calendars to pick from, before the coach exists. Read through any live staff
 * record's catalog (the calendar-sync panel's read); only the GHL provider's list is
 * tenant-wide, so any other provider offers nothing here.
 */
function TenantCalendarPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  disabled: boolean;
}) {
  const ids = useId();
  const catalog = useResource(
    useCallback(async (): Promise<StaffCalendarCatalog | null> => {
      const staff = await listStaff({ status: 'active' });
      const first = staff[0];
      return first ? getStaffCalendars(first.id) : null;
    }, []),
    [],
  );
  if (catalog.status === 'loading') {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
        Loading calendars…
      </Typography>
    );
  }
  const data = catalog.status === 'ready' ? catalog.data : null;
  // Only the sub-account provider's list is tenant-wide: neither off nor the per-coach provider.
  const tenantWide = data !== null && data.provider !== 'none' && data.provider !== 'google';
  const calendars = data !== null && tenantWide ? data.calendars : [];
  if (calendars.length === 0) {
    const reason =
      catalog.status === 'error'
        ? 'Calendars couldn’t load. Choose Create one for them, or Skip for now.'
        : data !== null && tenantWide && data.unavailableReason
          ? scrubBrand(data.unavailableReason)
          : 'There are no calendars to pick from. Choose Create one for them, or Skip for now.';
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
        {reason}
      </Typography>
    );
  }
  return (
    <FormControl fullWidth disabled={disabled} sx={{ mt: 1 }}>
      <InputLabel id={`${ids}-label`}>Calendar</InputLabel>
      <Select
        labelId={`${ids}-label`}
        label="Calendar"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {calendars.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** Each step's outcome, one line each. A calendar that did not link is partial, not an error. */
function ResultView({ result, onDone }: { result: CreateTeamMemberResult; onDone: () => void }) {
  const isCoach = result.staffId !== null;
  const [linked, setLinked] = useState(false);
  const calendar: CalendarResultLine = linked
    ? { ok: true, text: 'Calendar linked', fix: null }
    : calendarResultLine(result.calendar.status);
  const email = result.invitation.email;
  const partial = isCoach && !calendar.ok;
  return (
    <>
      <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }} data-testid="add-team-member-result">
        {partial ? (
          <Alert severity="warning" role="status" sx={{ mb: 2 }}>
            They’re added. One step still needs you.
          </Alert>
        ) : (
          <Alert severity="success" role="status" sx={{ mb: 2 }}>
            They’re added.
          </Alert>
        )}
        <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          <ResultLine
            ok={result.invitation.emailSent}
            text={
              result.invitation.emailSent
                ? `Invite sent to ${email}`
                : `Invite saved for ${email}, but the email didn’t go out. Resend it from the Invited tab.`
            }
          />
          {isCoach ? (
            <ResultLine ok text={servicesResultLine(result.servicesAdded.length)} />
          ) : null}
          {isCoach ? (
            <ResultLine ok={calendar.ok} text={calendar.text}>
              {calendar.fix === 'staff' && result.staffId !== null ? (
                <MuiLink component={RouterLink} to={`/staff/${result.staffId}`} sx={{ ml: 1 }}>
                  Fix
                </MuiLink>
              ) : null}
            </ResultLine>
          ) : null}
        </Stack>
        {isCoach && !linked && calendar.fix === 'picker' && result.staffId !== null ? (
          <LinkExistingCalendar staffId={result.staffId} onLinked={() => setLinked(true)} />
        ) : null}
      </Box>
      <Stack direction="row" sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="contained" onClick={onDone} sx={{ flex: 1 }}>
          Done
        </Button>
      </Stack>
    </>
  );
}

function ResultLine({ ok, text, children }: { ok: boolean; text: string; children?: ReactNode }) {
  return (
    <Stack component="li" direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <Icon
        name={ok ? 'check_circle' : 'error'}
        sx={{ fontSize: 20, color: ok ? 'success.main' : 'warning.main' }}
      />
      <Typography sx={{ fontSize: 14.5 }}>{text}</Typography>
      {children}
    </Stack>
  );
}

/**
 * "Calendar not linked yet": pick one of the calendars the provider offers this coach and link
 * it — the staff entity PUT the calendar-sync panel makes, re-sending the record as read (its
 * login link included, which the server applies unconditionally) plus the calendar and sync on.
 */
function LinkExistingCalendar({ staffId, onLinked }: { staffId: string; onLinked: () => void }) {
  const ids = useId();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const catalog = useResource(
    useCallback(() => (open ? getStaffCalendars(staffId) : Promise.resolve(null)), [open, staffId]),
    [open, staffId],
  );

  if (!open) {
    return (
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Fix: pick an existing calendar
        </Button>
      </Box>
    );
  }

  async function handleLink() {
    if (choice === '') return;
    setSaving(true);
    setError(null);
    try {
      const staff = await getStaffMember(staffId);
      if (staff === null) throw new Error('missing');
      const input: StaffInput = { firstName: staff.firstName, lastName: staff.lastName };
      if (staff.email !== undefined) input.email = staff.email;
      if (staff.phone !== undefined) input.phone = staff.phone;
      input.authUserId = staff.authUserId;
      input.calendarId = choice;
      input.calendarSyncEnabled = true;
      await updateStaff(staffId, input);
      onLinked();
    } catch (err) {
      setError(
        staffCalendarSyncErrorMessage(err) ??
          serverMessage(err) ??
          'The calendar couldn’t be linked. Fix it on the staff page.',
      );
    } finally {
      setSaving(false);
    }
  }

  const calendars = catalog.status === 'ready' && catalog.data ? catalog.data.calendars : [];
  return (
    <Box sx={{ mt: 2 }}>
      {catalog.status === 'loading' ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Loading calendars…
        </Typography>
      ) : calendars.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {catalog.status === 'ready' && catalog.data?.unavailableReason
            ? scrubBrand(catalog.data.unavailableReason)
            : 'There are no calendars to pick from yet.'}{' '}
          <MuiLink component={RouterLink} to={`/staff/${staffId}`}>
            Open the staff page
          </MuiLink>
        </Typography>
      ) : (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <FormControl fullWidth size="small" disabled={saving}>
            <InputLabel id={`${ids}-label`}>Existing calendar</InputLabel>
            <Select
              labelId={`${ids}-label`}
              label="Existing calendar"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
            >
              {calendars.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => void handleLink()}
            disabled={saving || choice === ''}
          >
            {saving ? 'Linking…' : 'Link'}
          </Button>
        </Stack>
      )}
      {error ? (
        <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
    </Box>
  );
}
